import React, { useState, useEffect } from "react";
import { supabase } from "../../Supabase/SupabaseClient";
import { useNavigate } from "react-router-dom";
import { FiPlus, FiMinus, FiTrash2 } from "react-icons/fi";
import BrandArrow from "../../Components/ui/BrandArrow";
import "./Compra.css";
import Adicional from "../../Pages/Clients/Adicional";
import Footer from "../../Components/Footer/Footer";

const Compra = () => {
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(""); // "efectivo" o "transferencia"
  const [userData, setUserData] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const navigate = useNavigate();

  // Fetch cart items with paquete and adicional
  const fetchCartItems = async () => {
    setError(null);
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user) {
        setError("Debes iniciar sesión para ver tu carrito.");
        return;
      }

      // Guardar el usuario completo
      setUserData(user);

      const { data: carritoData, error: carritoError } = await supabase
        .from("carrito")
        .select("*")
        .eq("usuario_id", user.id);
      if (carritoError) throw carritoError;

      const paqueteIds = carritoData.map((i) => i.paquete_id).filter(Boolean);
      const adicionalIds = carritoData.map((i) => i.adicional_id).filter(Boolean);
      const servicioIds = carritoData
        .map((i) => i.servicio_id)
        .filter((id) => id != null);

      const [paquetesRes, adicionalesRes, serviciosRes] = await Promise.all([
        paqueteIds.length
          ? supabase
              .from("paquetes")
              .select("id, nombre, descripcion, precio")
              .in("id", paqueteIds)
          : Promise.resolve({ data: [] }),
        adicionalIds.length
          ? supabase
              .from("adicional")
              .select("id, nombre, descripcion, precio")
              .in("id", adicionalIds)
          : Promise.resolve({ data: [] }),
        servicioIds.length
          ? supabase
              .from("servicios")
              .select("id, nombre, descripcion, precio")
              .in("id", servicioIds)
          : Promise.resolve({ data: [] }),
      ]);

      const paquetesData = paquetesRes.data || [];
      const adicionalesData = adicionalesRes.data || [];
      const serviciosData = serviciosRes.data || [];

      const items = carritoData.map((ci) => ({
        id: ci.id,
        cantidad: ci.cantidad,
        servicio: serviciosData.find((s) => s.id === ci.servicio_id) || null,
        paquete: paquetesData.find((p) => p.id === ci.paquete_id) || null,
        adicional: adicionalesData.find((a) => a.id === ci.adicional_id) || null,
      }));

      setCartItems(items);
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al cargar el carrito.");
    }
  };

  // Calculate total whenever items change
  useEffect(() => {
    const total = cartItems.reduce((sum, item) => {
      const precioServicio = Number(item.servicio?.precio ?? 0) || 0;
      const precioPaquete = item.paquete?.precio ?? 0;
      const precioAdic = item.adicional?.precio ?? 0;
      return sum + (precioServicio + precioPaquete + precioAdic) * item.cantidad;
    }, 0);
    setTotalAmount(total);
  }, [cartItems]);

  useEffect(() => {
    fetchCartItems();
  }, []);

  // Suscribirse a cambios en la tabla 'carrito' para actualizar automáticamente
  useEffect(() => {
    if (!userData?.id) return;

    const channel = supabase
      .channel(`cart-user-${userData.id}`)
      .on(
        "postgres_changes",
        {
         event: "INSERT",
          schema: "public",
          table: "carrito",
          filter: `usuario_id=eq.${userData.id}`,
        },
        (payload) => {
          console.log("Carrito cambio:", payload);
          fetchCartItems();
        }
      )
      .subscribe((status) => {
        console.log("Suscripción carrito status:", status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userData?.id]);

  const updateItemQuantity = async (itemId, newQty) => {
    if (newQty < 1) return;
    await supabase
      .from("carrito")
      .update({ cantidad: newQty })
      .eq("id", itemId);
    setCartItems((items) =>
      items.map((i) => (i.id === itemId ? { ...i, cantidad: newQty } : i))
    );
  };

 const removeCartItem = async (itemId) => {
  setCartItems((items) => items.filter((i) => i.id !== itemId));
  
  const { error } = await supabase
    .from("carrito")
    .delete()
    .eq("id", itemId);
    
  if (error) {
    console.error("Error al eliminar:", error);
    fetchCartItems();
  }
};



  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  const processCheckout = async () => {
    if (!paymentMethod) {
      setError("Por favor selecciona un método de pago");
      return;
    }

    if (!userData || !userData.id) {
      setError("No se pudo identificar tu sesión. Por favor, inicia sesión nuevamente.");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // 1. Obtener información del usuario para el mensaje
      const { data: userProfile, error: profileError } = await supabase
        .from("usuarios")
        .select("nombre, telefono, email")
        .eq("id", userData.id)
        .single();

      if (profileError) {
        console.error("Error al obtener perfil:", profileError);
        throw profileError;
      }

      // 2. Crear una orden por cada producto del carrito, todas del mismo pedido.
      //    `pedido_id` agrupa las filas de esta compra para que Contrato.js pueda
      //    recuperar el pedido completo (antes solo leía la última fila).
      const pedidoId = crypto.randomUUID();

      const ordersPromises = cartItems.map(async (item) => {
        const precioUnitario =
          (Number(item.servicio?.precio) || 0) +
          (Number(item.paquete?.precio) || 0) +
          (Number(item.adicional?.precio) || 0);

        const orderData = {
          usuario_id: userData.id,
          pedido_id: pedidoId,
          montoTotal: precioUnitario * item.cantidad,
          metodoPago: paymentMethod,
          estado: "pendiente",
          servicio_id: item.servicio?.id || null,
          paquete_id: item.paquete?.id || null,
          adicional_id: item.adicional?.id || null,
          cantidad: item.cantidad,
          precio_unitario: precioUnitario
          // fecha_creacion se generará automáticamente por Supabase si está configurado como tipo TIMESTAMP con DEFAULT now()
        };
        
        const { data, error } = await supabase
          .from("ordenes")
          .insert(orderData)
          .select();

        if (error) {
          console.error("Error al crear orden:", error);
          throw error;
        }
        
        return data[0];
      });

      // Ejecutar todas las promesas de inserción
      const orders = await Promise.all(ordersPromises);
      
      // 3. Enviar mensaje por WhatsApp con todas las órdenes del pedido
      sendWhatsAppMessage(userProfile, orders, pedidoId);

      // 4. Limpiar el carrito
      await supabase
        .from("carrito")
        .delete()
        .eq("usuario_id", userData.id);

      setSuccessMessage("¡Tu orden ha sido registrada! Te hemos enviado un mensaje por WhatsApp con los detalles del pago.");
      setCartItems([]);
      
      // Redirigir después de 3 segundos
      setTimeout(() => {
        navigate("/reserva");
      }, 2500);
      
    } catch (err) {
      console.error("Error general:", err);
      setError(err.message || "Error al procesar el pago. Inténtalo nuevamente.");
    } finally {
      setIsProcessing(false);
    }
  };

  const sendWhatsAppMessage = (userProfile, orders, pedidoId) => {
    // Número de WhatsApp del negocio
    const businessPhone = "5493816818108"; // Formato: código de país + número sin el +
    
    // Crear lista de items
    const itemsList = cartItems.map(item => {
      const producto = item.servicio || item.paquete || item.adicional;
      const nombre = producto?.nombre || 'Servicio';
      const precio = Number(producto?.precio) || 0;
      return `${nombre} x${item.cantidad} - $${(precio * item.cantidad).toFixed(2)}`;
    }).join("\n- ");
    
    // Referencias de las filas de ordenes creadas para este pedido
    const idsList = (orders || []).map((o) => `#${o.id}`).join(", ");
    const pedidoRef = pedidoId ? ` (pedido ${pedidoId.slice(0, 8)})` : "";
    
    // Crear mensaje
    const message = 
      `*Nueva Orden${pedidoRef}*\n` +
      `Filas de ordenes: ${idsList}\n\n` +
      `Cliente: ${userProfile.nombre}\n` +
      `Método de pago: ${paymentMethod === 'efectivo' ? 'Efectivo' : 'Transferencia'}\n\n` +
      `*Detalle de la compra:*\n- ${itemsList}\n\n` +
      `*Total a pagar: $${totalAmount.toFixed(2)}*\n\n` +
      `Por favor, indícame cómo proceder con el ${paymentMethod === 'efectivo' ? 'pago en efectivo' : 'pago por transferencia'}.`;
    
    // Codificar el mensaje para URL
    const encodedMessage = encodeURIComponent(message);
    
    // Crear URL de WhatsApp
    const whatsappUrl = `https://wa.me/${businessPhone}?text=${encodedMessage}`;
    
    // Abrir WhatsApp en una nueva ventana
    window.open(whatsappUrl, '_blank');
  };

  // Separate servicios and adicionales
  const servicios = cartItems.filter((item) => item.servicio);
  const adicionales = cartItems.filter((item) => item.adicional);

  return (
    <>
    <div className="compra-container">
      <h2>Resumen de Compra y Reserva de Turno</h2>
      {error && (
        <div className="error-message" role="alert">
          <span className="message-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </span>
          <div className="message-body">
            <strong className="message-title">Atención</strong>
            <p>{error}</p>
          </div>
        </div>
      )}
      {successMessage && (
        <div className="success-message" role="status">
          <span className="message-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </span>
          <div className="message-body">
            <strong className="message-title">Pedido registrado</strong>
            <p>{successMessage}</p>
          </div>
        </div>
      )}

      {/* Sección Servicios */}
      <div className="cart-section">
        <h3>Servicios</h3>
        {servicios.length === 0 ? (
          <p>No tenés servicios en el carrito.</p>
        ) : (
          servicios.map((item) => {
            const precio = Number(item.servicio.precio) || 0;
            const subtotal = precio * item.cantidad;
            return (
              <div key={item.id} className="cart-item">
                <div className="cart-package-info">
                  <h4>{item.servicio.nombre}</h4>
                  {item.servicio.descripcion && <p>{item.servicio.descripcion}</p>}
                  <div className="quantity-controls">
                    <button
                      onClick={() => updateItemQuantity(item.id, item.cantidad - 1)}
                      disabled={item.cantidad <= 1}
                    >
                      <FiMinus />
                    </button>
                    <span>{item.cantidad}</span>
                    <button
                      onClick={() => updateItemQuantity(item.id, item.cantidad + 1)}
                    >
                      <FiPlus />
                    </button>
                  </div>
                  <p>Precio unitario: ${precio.toFixed(2)}</p>
                  <p>Subtotal: ${subtotal.toFixed(2)}</p>
                </div>
                <button onClick={() => removeCartItem(item.id)}>
                  <FiTrash2 />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Sección Adicionales */}
      <div className="cart-section">
        <h3>Adicionales</h3>
        {adicionales.length === 0 ? (
          <p>No tienes adicionales en el carrito.</p>
        ) : (
          adicionales.map((item) => {
            const precio = item.adicional.precio;
            const subtotal = precio * item.cantidad;
            return (
              <div key={item.id} className="cart-item">
                <div className="cart-addon-info">
                  <h4>{item.adicional.nombre}</h4>
                  <p>{item.adicional.descripcion}</p>
                  <div className="quantity-controls">
                    <button
                      onClick={() => updateItemQuantity(item.id, item.cantidad - 1)}
                      disabled={item.cantidad <= 1}
                    >
                      <FiMinus />
                    </button>
                    <span>{item.cantidad}</span>
                    <button
                      onClick={() => updateItemQuantity(item.id, item.cantidad + 1)}
                    >
                      <FiPlus />
                    </button>
                  </div>
                  <p>Precio unitario: ${precio.toFixed(2)}</p>
                  <p>Subtotal: ${subtotal.toFixed(2)}</p>
                </div>
                <button onClick={() => removeCartItem(item.id)}>
                  <FiTrash2 />
                </button>
              </div>
            );
          })
        )}
      </div>

    <Adicional />

      {/* Sección Métodos de Pago */}
      <div className="payment-section">
        <h3>Métodos de Pago</h3>
        <div className="payment-options">
          <div className="payment-option">
            <input
              type="radio"
              id="efectivo"
              name="paymentMethod"
              value="efectivo"
              checked={paymentMethod === "efectivo"}
              onChange={handlePaymentMethodChange}
            />
            <label htmlFor="efectivo">Efectivo</label>
          </div>
          <div className="payment-option">
            <input
              type="radio"
              id="transferencia"
              name="paymentMethod"
              value="transferencia"
              checked={paymentMethod === "transferencia"}
              onChange={handlePaymentMethodChange}
            />
            <label htmlFor="transferencia">Transferencia Bancaria</label>
          </div>
        </div>
        {paymentMethod === "efectivo" && (
          <div className="payment-info">
            <p>Al seleccionar efectivo, te contactarás por WhatsApp para coordinar el pago.</p>
          </div>
        )}
        {paymentMethod === "transferencia" && (
          <div className="payment-info">
            <p>Al seleccionar transferencia bancaria, debes compartir comprobante por WhatsApp.</p>
            <h4>Datos bancarios</h4>
            <ul className="bank-details">
              <li><strong>Alias:</strong> PASCUA.LABIO.BARRA</li>
              <li><strong>CBU:</strong> 03402117082211022407003</li>
              <li><strong>Banco:</strong> BANCO PATAGONIA</li>
              <li><strong>NOMBRE:</strong> MARIA SOLEDAD COSTILLA</li>
            </ul>
          </div>
        )}
      </div>

      <div className="cart-summary">
        <h3>Total: ${totalAmount.toFixed(2)}</h3>
        <button
          className="checkout-button"
          onClick={processCheckout}
          disabled={isProcessing || cartItems.length === 0 || !paymentMethod}
        >
          {isProcessing ? "Procesando..." : "Confirmar Pedido"}
          <span className="checkout-button-arrow" aria-hidden="true">
            <BrandArrow />
          </span>
        </button>
      </div>
    </div>
      <Footer />
    </>
  );
};

export default Compra;
